<script setup lang="ts">
import type { CounterSize, CounterVariant } from './types'

type Props = {
  size?: CounterSize
  variant?: CounterVariant
  value: number
  min?: number
  max?: number
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'sm',
  min: 0,
  max: undefined,
})

const emit = defineEmits(['onIncrease', 'onDecrease'])

const componentClasses = computed(() => {
  return [
    'app-counter',
    `app-counter--size-${props.size}`,
    `app-counter--variant-${props.variant}`,
    {
      'app-counter--disabled': props.disabled,
    },
  ]
})

const isHidden = computed(() => props.value <= props?.min)
</script>

<template>
  <div :class="componentClasses" :aria-disabled="disabled">
    <the-button
      data-test-id="counter-minus"
      class="app-counter__button"
      :class="{ 'app-counter__button--hidden': isHidden }"
      variant="transparent"
      radius="lg"
      icon="minus"
      aria-label="Уменьшить"
      type="button"
      :size="size"
      @click="emit('onDecrease')"
    />

    <div v-show="!isHidden" class="app-counter__number">
      {{ value }}
    </div>

    <the-button
      data-test-id="counter-plus"
      class="app-counter__button"
      :class="{ 'app-counter__button--hidden': isHidden }"
      variant="transparent"
      radius="lg"
      icon="plus"
      aria-label="Увеличить"
      type="button"
      :size="size"
      @click="emit('onIncrease')"
    />

    <the-button
      v-show="isHidden"
      data-test-id="counter-add"
      class="app-counter__button app-counter__button--overlay"
      variant="transparent"
      radius="lg"
      icon="plus"
      aria-label="Добавить"
      type="button"
      :size="size"
      :ripple="false"
      @click="emit('onIncrease')"
    />
  </div>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-counter {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 800;
  line-height: 16px;
  color: var(--ui-text-primary);
  border-radius: 100px;
  transition: background-color 0.2s ease-in-out;

  @include media('>=desktop-mini') {
    font-size: 16px;
    line-height: 20px;
  }

  &__number {
    width: 24px;
    margin: 0 auto;
    text-align: center;
    user-select: none;
  }

  &__button {
    transition: all 0.2s ease-in-out;

    &--overlay {
      position: absolute;
      width: 100% !important;
    }

    &--hidden {
      pointer-events: none;
      visibility: hidden;
      opacity: 0;
    }
  }

  &--size-sm {
    min-width: 108px;
  }

  &--size-md {
    min-width: 160px;
  }

  &--variant-primary {
    background-color: var(--ui-grey);

    @include media('>=desktop-mini') {
      &:hover {
        background-color: var(--ui-grey--hover);
      }
    }
  }

  &--variant-secondary {
    background-color: var(--ui-white);
  }

  &--disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}
</style>
